import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@aqarmap/aqarmap-ui';
import { useAppDispatch } from '~/redux/hooks';
import useBreakpoint from '~/hooks/useBreakpoint';
import {
  openModal,
  setFooterDisplay,
} from '~/redux/features/uiState/uiStateSlice';
import useQuery from '~/hooks/useQuery';
import { useFullRoutes, useWallet } from '~/hooks';
import { formatNumber } from '~/lib';
import { useGetSubscribePlanQuery } from '~/redux/api/subscriptionApiSlice';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';
import PlanSummary from '~/pages/Subscription/Checkout/PlanSummary';
import { SelectedPaymentType } from '~/types/paymentTypes';
import { UserCardFormHandle } from '~/components/UserCardForm/types';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import { PlanType } from '~/types/subscriptionTypes';
import '~/cardsIcons.css';
import { RequestStatusEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { sendGTMEvent } from '~/utils';
import PlanToggle from '~/pages/Subscription/Checkout/PlanToggle';

const MONTHLY_PLAN_ID = 3;
const YEARLY_PLAN_ID = 4;

export default function Checkout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { trial } = useQuery();
  const { status } = useQuery();
  const { SUBSCRIPTION_URL, MY_ACCOUNT_URL } = useFullRoutes();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const { mobileScreen } = useBreakpoint();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const userCardFormRef = useRef<UserCardFormHandle>(null);
  const currentPlanId = Number(id);
  const showPlanToggle =
    currentPlanId === MONTHLY_PLAN_ID || currentPlanId === YEARLY_PLAN_ID;

  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);
  const { walletBalance } = useWallet({});
  const [postPaymentPay, { isLoading: isSubscribeLoading }] =
    usePostPaymentPayMutation();

  const {
    data: plan,
    isLoading: isPlanLoading,
    isSuccess: isPlanSuccess,
  } = useGetSubscribePlanQuery({ id, locale });
  const { data: monthlyPlan } = useGetSubscribePlanQuery(
    { id: MONTHLY_PLAN_ID.toString(), locale },
    { skip: !showPlanToggle },
  );
  const { data: yearlyPlan } = useGetSubscribePlanQuery(
    { id: YEARLY_PLAN_ID.toString(), locale },
    { skip: !showPlanToggle },
  );

  const isFreeTrial =
    Boolean(trial) && plan?.has_trial && plan?.trial_days !== null;
  const amountDue = isFreeTrial ? 0 : Number(plan?.price || 0);
  const payWithWallet = !!selectedPayment?.payWithWallet;
  const payWithCash = !!selectedPayment?.payWithCash;
  const hasSelectedPayment =
    payWithWallet || !!selectedPayment?.isNew || Boolean(selectedPayment?.id);
  const hasEnoughBalance = walletBalance >= amountDue;
  const isWalletBalanceInsufficient = payWithWallet && !hasEnoughBalance;
  const isSubmitDisabled =
    !hasSelectedPayment ||
    payWithCash ||
    isSubscribeLoading ||
    isPayfortLoading ||
    isWalletBalanceInsufficient;
  const duration = Number(plan?.duration_days) / 30;
  const fees = `${formatNumber(Number(plan?.price))} ${t('egp')}/${duration === 1 ? ` ${t('month')}` : ` ${duration} ${t('months')}`}`;

  const handlePayWithSavedCard = async () => {
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.SAVED_CARD,
      operationType: PaymentOperationTypeEnum.SUBSCRIPTION,
      cardId: selectedPayment?.id,
      subscriptionId: plan?.id,
      isTrial: isFreeTrial,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
    } else if (data?.status === '3ds_required' && data?.['3ds_url']) {
      window.location.href = data['3ds_url'];
    } else {
      if (error && 'data' in error && (error.data as any)?.message) {
        setCheckoutError((error.data as any).message);
      } else {
        setCheckoutError(t('somethingWrong'));
      }
    }
  };

  const handlePayWithWallet = async () => {
    if (isWalletBalanceInsufficient) return;
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.WALLET,
      operationType: PaymentOperationTypeEnum.SUBSCRIPTION,
      subscriptionId: plan?.id,
      isTrial: isFreeTrial,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
    }
    if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError((error.data as any).message);
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const handlePayWithCash = async () => {
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.CASH,
      operationType: PaymentOperationTypeEnum.SUBSCRIPTION,
      subscriptionId: plan?.id,
      isTrial: isFreeTrial,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
    } else if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError((error.data as any).message);
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const onCheckoutSubmit = () => {
    const isNewCard = selectedPayment?.isNew;
    const isValidPlan = id && Boolean(plan?.id);
    const isValidUserCardForm = userCardFormRef.current;
    if (isValidPlan) {
      sendGTMEvent({ event: 'gtm-subscription_pay_now_clicked' });
      if (payWithWallet) {
        handlePayWithWallet();
      } else if (payWithCash) {
        handlePayWithCash();
      } else if (isNewCard && isValidUserCardForm) {
        setIsPayfortLoading(true);
        userCardFormRef.current.submitUserCard({
          operationType: PaymentOperationTypeEnum.SUBSCRIPTION,
          subscriptionId: Number(plan?.id),
          isTrial: isFreeTrial,
          returnTo: `${process.env.REACT_APP_BASE_URL}${SUBSCRIPTION_URL.CHECKOUT(id)}`,
        });
      } else {
        handlePayWithSavedCard();
      }
    }
  };

  const handleStatusFallback = () => {
    switch (status) {
      case RequestStatusEnum.FAILED:
        setCheckoutError(t('somethingWrong'));
        setSelectedPayment({
          isNew: true,
          id: 0,
          payWithWallet: false,
        });
        break;
      case RequestStatusEnum.SUCCESS:
        window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
        break;
    }
  };

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
  }, [mobileScreen]);

  useEffect(() => {
    handleStatusFallback();
  }, [status]);

  const handlePlanChange = (planId: number) => {
    navigate(SUBSCRIPTION_URL.CHECKOUT(planId));
  };

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col-reverse md:flex-row gap-4x justify-between px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('paymentDetails')}
          enableWallet={true}
          enableCash={false}
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => setCheckoutError(t('somethingWrong')),
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          isLoading={isPlanLoading || !isPlanSuccess}
          enableTermsAndConditions={true}
          totalFeesLabel={t('totalFees')}
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isSubscribeLoading || isPayfortLoading,
            disabled: isSubmitDisabled,
          }}
          fees={isFreeTrial ? `0.00 ${t('egp')}` : fees}
          summary={
            <PlanSummary
              plan={plan as PlanType}
              isFreeTrial={isFreeTrial}
              toggleContent={
                showPlanToggle && monthlyPlan && yearlyPlan ? (
                  <PlanToggle
                    selectedPlanId={currentPlanId}
                    monthlyPlan={monthlyPlan}
                    yearlyPlan={yearlyPlan}
                    onPlanChange={handlePlanChange}
                  />
                ) : undefined
              }
            />
          }
          title={t('planSummary')}
        />
        <div className="fixed bottom-0 left-0 z-[40] flex w-full flex-col items-center gap-1.5x border-t border-gray bg-white px-2x pt-2x pb-3x md:hidden">
          <Button
            type="button"
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isSubscribeLoading || isPayfortLoading}
            disabled={isSubmitDisabled}
          >
            {t('pay')}
          </Button>
          <p className="text-center text-caption-1 text-gray__dark_1">
            <Trans
              i18nKey="termsAndConditions"
              t={t}
              components={{
                tag: (
                  <button
                    className="text-title-5 text-blue hover:underline"
                    onClick={() => {
                      dispatch(openModal('termsAndConditionsModal'));
                    }}
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
}
